// ** React Imports
import { useContext, useMemo } from "react";
import { matchPath, useLocation, useParams } from "react-router-dom";

// ** Redux & Store & Actions
import { useDispatch, useSelector } from "react-redux";
import { getUserProfileMain } from "store/modules/user-profile/actions/get-user-profile-main.action";

// ** Third Party Components
import loadable from "@loadable/component";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import pseudoFormat from "utils/pseudoFormat";

// ** Custom Components
import Login from "components/user/Login";
import Text from "components/typography/Text";
import Icon from "components/global/Icon";
import Block from "components/layout/Block";
import OnlineCircle from "components/user/OnlineCircle";
import DynamicLockIcon from "components/icons/DynamicLockIcon_V2";
import DatePresenter from "components/other/DatePresenter";
import ConvertLocationProfile from "components/other/ConvertLocationProfile";
const HeroSectionV2 = loadable(
	() =>
		import(
			"components/layout/PresentationTemplate/HeroSection/HeroSection_V2"
		),
);
import HeroSectionDesktopNew from "components/layout/PresentationTemplate/HeroSection/HeroSectionDesktopNew";
import UserAvatar from "components/user/UserAvatar";

// ** Utils & Helpers
import generatePath from "utils/generatePath";
import { VIP_STATUS } from "helpers/constants";
import { processCoupleSexWithAgeNewView } from "utils/processCouple";

// ** Contexts
import DeviceTypeContext from "contexts/deviceTypeContext";

// ** Routes
import containers from "containers";

// ** SVG Imports
import { ReactComponent as RegularHeart } from "resources/icons/regular/heart.svg";
import { ReactComponent as SolidEye } from "resources/icons/solid/eye.svg";

// #####################################################

const StyledIconContainer = styled(Text)`
	display: flex;
	align-items: center;
	color: var(--dz-sys-icon-background-default);
	font-size: 0.8rem;

	svg {
		color: var(--dz-sys-icon-background-default);
		width: 12px;
	}
`;

export const StyledInfoContainer = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: flex-end;
	width: 100%;
	height: max-content;
	margin-left: 1rem;
	color: var(--dz-sys-text-color-always-light);
`;

// #####################################################

const UserHeroSection = ({ constantPadding, isCityWithNewLink }) => {
	const { t } = useTranslation(["common", "countries", "userProfile"]);
	const isAuth = useSelector((state) => state.globalCommon.isAuth);

	const { isPhone, isDesktop } = useContext(DeviceTypeContext);

	const location = useLocation();
	const dispatch = useDispatch();

	const language = useSelector((state) => state.globalCommon.language);

	const isDarkMode = useSelector(
		(state) => state.globalCommon?.darkMode?.isDarkMode,
	);

	const globalUserShowStatusVip = useSelector(
		(state) => state.globalUser.showStatusVip,
	);
	const userMain = useSelector((state) => state.userProfileMain);
	const userLoaded = useSelector((state) => state.userProfileMain.loaded);
	const userId = useSelector((state) => state.userProfileMain.id);
	const userAvatarMedia = useSelector(
		(state) => state.userProfileMain?.avatarMedia,
	);
	const userAvatar = useSelector((state) => state.userProfileMain.avatar);

	const userLogin = useSelector((state) => state.userProfileMain.login);
	const userFrozen = useSelector((state) => state.userProfileMain.frozen);
	const userVip = useSelector((state) => state.userProfileMain.vip);
	const userOnline = useSelector((state) => state.userProfileMain.online);
	const userProfileStatus = useSelector(
		(state) => state.userProfileMain.profileStatus,
	);
	const userPrivateData = useSelector(
		(state) =>
			state.userProfileMain.private_data ||
			state.userProfileMain.privateData,
	);

	const currentLoggedUser = useSelector((state) => state.globalUser);
	const isCurrentLoggedUser = currentLoggedUser.id === userId;
	const correctOnline = isCurrentLoggedUser
		? currentLoggedUser.online
		: userOnline;
	const correctStatus = isCurrentLoggedUser
		? currentLoggedUser.profileStatus
		: userProfileStatus;

	// ** Odczytanie parametrów z adresu URL
	const params = useParams();
	const { login } = params || {};

	// ** Login użytkownika z adresu URL
	const userLoginFromUrl = login?.toLowerCase();

	// ** Funkcja generuje link do profilu użytkownika.
	const userProfileLink = generatePath(containers.user.path, {
		login: userLoginFromUrl,
	});

	// ** Hook useMemo - Link z awataru użytkownika, w zależności od tego
	// ** czy użytkownik jest zalogowany, posiada domyślny awatar lub własny
	// ** link prowadzi w różne miejsca
	const avatarLink = useMemo(() => {
		const hasDefaultAvatar = userAvatarMedia?.mediaType === "default";
		if (isAuth) {
			if (hasDefaultAvatar) {
				return userProfileLink;
			}
			return {
				hash: generatePath(containers.modal.path, {
					contentId: userAvatarMedia?.contentId,
					login: userLogin?.toLowerCase(),
					type: userAvatarMedia?.mediaType,
				}),
			};
		}
		return "/";
	}, [userLoaded, userAvatarMedia, userLogin, userProfileLink]);

	const getProfileDetailsMergeFn = () =>
		dispatch(getUserProfileMain({ login, isAuth, throwError: "true" }));

	const secondLine_Age = useMemo(() => {
		if (userMain?.sex === "couple") {
			if (userMain?.partners?.age[0] && userMain?.partners?.age[1]) {
				return (
					<span style={{ marginLeft: "0.25rem" }}>
						{processCoupleSexWithAgeNewView({
							sex1: userMain.partners.sex[0],
							age1: userMain.partners.age[0],
							sex2: userMain.partners.sex[1],
							age2: userMain.partners.age[1],
							t,
						})}
					</span>
				);
			} else if (
				userMain?.partners?.sex[0] &&
				userMain?.partners?.sex[1]
			) {
				return (
					<span style={{ marginLeft: "0.25rem" }}>
						<span style={{ marginRight: "0.25rem" }}>
							({t(`sexOptions.${userMain?.partners?.sex[0]}`)},
						</span>
						<span>
							{t(`sexOptions.${userMain?.partners?.sex[1]}`)})
						</span>
					</span>
				);
			} else return "";
		}
		return userMain?.partners?.age[0] ? (
			<span style={{ marginLeft: "0.25rem" }}>
				{userMain.partners.age[0]} {t("common:years")}
			</span>
		) : (
			""
		);
	}, [language]);

	const secondLine_Sex = useMemo(() => {
		return (
			<span>
				{t(`sexOptions.${userMain.sex}`)}
				{secondLine_Age && userMain.sex !== "couple" ? "," : ""}
			</span>
		);
	}, [language]);

	const secondLine = useMemo(() => {
		return (
			<Block flex alignCenter flexWrap>
				<span style={{ marginRight: "0.25rem" }}>
					{secondLine_Sex}
					{secondLine_Age}
					<span>,</span>
				</span>
				<span className="user-hero-section-location">
					<ConvertLocationProfile
						location={userMain.location}
						url={userMain.location.urlSearch}
						asHeroSectionLinkNew={isCityWithNewLink}
						asHeroSectionLink={!isCityWithNewLink}
						withoutRegion
					/>
				</span>
			</Block>
		);
	}, [secondLine_Sex, secondLine_Age]);

	// #####################################################

	if (isPhone) {
		return (
			<HeroSectionV2
				avatarSrc={userAvatarMedia?.file}
				constantPadding={constantPadding}
				link={avatarLink}
			>
				<Block flex withColumnGap={"0.5rem"}>
					<Login
						onClick={() => {
							if (
								matchPath(location.pathname, {
									path: `/users/${userLoginFromUrl}`,
									exact: true,
								})
							) {
								getProfileDetailsMergeFn();
							}
						}}
						login={userLogin}
						size="base"
						color="#F2F2F2"
						className="user-hero-section-login"
						link
						append={
							(correctOnline || userPrivateData) && (
								<Block
									flex
									style={{
										marginBottom: "0.125rem",
									}}
								>
									{correctStatus !== 4 && (
										<Block ml={2}>
											<OnlineCircle
												online={correctOnline}
												status={correctStatus}
												size="large"
											/>
										</Block>
									)}
									{userPrivateData && (
										<DynamicLockIcon
											customFill="whiteAlways"
											customHeight={"18px"}
											customWidth={"20px"}
											customVerticalAlign={"text-top"}
											customMargin="0 0 0 0.5rem"
										/>
									)}
								</Block>
							)
						}
						vip={
							userFrozen === true
								? false
								: isCurrentLoggedUser
									? globalUserShowStatusVip
										? userVip
										: userVip > VIP_STATUS.VERIFIED
											? VIP_STATUS.VERIFIED
											: userVip
									: userVip
						}
						vipProps={{
							height: 17.5,
							whiteMode: !isDarkMode,
							marginBottom: 0.125,
							marginLeft: 0.5,
							marginLeftUnverified: "0.25rem",
							lighten: true,
							showUnverified: true,
							noPad: true,
							style: {
								position: "absolute",
								right: 0,
							},
						}}
						isProfile
					/>
				</Block>
				{!userMain?.status?.blockMe &&
					!userMain?.status?.blockByMe &&
					!userMain?.frozen && (
						<>
							<Block zIndex={3}>
								<Text color="whiteAlways" size="1rem">
									{secondLine}
								</Text>
							</Block>

							{(userMain.statistics.likes > 0 ||
								userMain.statistics.userFollowers > 0) && (
								<Block flex>
									{userMain.statistics.likes > 0 && (
										<StyledIconContainer
											style={{ marginRight: "1rem" }}
										>
											<Text mr={1}>
												<Icon
													svgIconComponent={
														RegularHeart
													}
												/>
											</Text>
											{pseudoFormat(
												userMain.statistics.likes ||
													null,
											)}
										</StyledIconContainer>
									)}
									{userMain.statistics.userFollowers > 0 && (
										<StyledIconContainer>
											<Text mr={1}>
												<Icon
													svgIconComponent={SolidEye}
												/>
											</Text>
											{pseudoFormat(
												userMain.statistics
													.userFollowers || null,
											)}
										</StyledIconContainer>
									)}
								</Block>
							)}

							<Block
								style={{
									fontSize: "0.8rem",

									color: "var(--dz-sys-icon-background-default)",
								}}
							>
								{(correctOnline === 0 ||
									correctStatus === 4) && (
									<Text
										style={{
											display: "inline-block",
										}}
										mr={1}
									>
										{t(`common:lastActivity`)}
										{": "}
										<DatePresenter
											date={userMain?.active}
											dateOnly
											comma
										/>
									</Text>
								)}
								<Text
									mr={1}
									style={{
										display: "inline-block",
									}}
								>
									{t(`common:accountSince`)}:
								</Text>
								<Text
									mr={1}
									style={{
										display: "inline-block",
									}}
								>
									<DatePresenter
										date={userMain?.create}
										dateOnly
									/>
								</Text>
							</Block>
						</>
					)}
			</HeroSectionV2>
		);
	}
	return (
		<HeroSectionDesktopNew avatarSrc={userAvatar}>
			<Block data-component="HeroSection_Content" flex alignEnd>
				<UserAvatar
					userId={userId}
					link
					size={isDesktop ? "xlarge" : "large"}
					to={avatarLink}
					login={userLogin}
					src={userAvatar}
					online={userOnline}
					status={userProfileStatus}
					customBorderRadius={"50%"}
					customBoxShadow={"var(--dz-sys-button-shadow)"}
					withAuthModal
				/>
				<StyledInfoContainer
					data-component="HeroSection_Info"
					flex
					column
					justifyEnd
				>
					<Block
						flex
						withColumnGap={"0.5rem"}
						onClick={() => {
							if (
								matchPath(location.pathname, {
									path: `/users/${userLoginFromUrl}`,
									exact: true,
								})
							) {
								getProfileDetailsMergeFn();
							}
						}}
					>
						<Login
							login={userLogin}
							size="base"
							color="#F2F2F2"
							className="user-hero-section-login"
							link
							append={
								(correctOnline || userPrivateData) && (
									<Block
										flex
										style={{
											marginBottom: "0.125rem",
										}}
									>
										{userPrivateData && (
											<DynamicLockIcon
												privateData
												customFill="whiteAlways"
												customHeight={"17px"}
												customWidth={"17px"}
												customVerticalAlign={"text-top"}
												customMargin="0 0 0 0.5rem"
											/>
										)}
									</Block>
								)
							}
							vip={
								userFrozen === true
									? false
									: isCurrentLoggedUser
										? globalUserShowStatusVip
											? userVip
											: userVip > VIP_STATUS.VERIFIED
												? VIP_STATUS.VERIFIED
												: userVip
										: userVip
							}
							vipProps={{
								height: 17.5,
								whiteMode: !isDarkMode,
								marginBottom: 0.125,
								marginLeft: 0.5,
								marginLeftUnverified: "0.25rem",
								lighten: true,
								showUnverified: true,
								noPad: true,
								style: {
									position: "absolute",
									right: 0,
								},
							}}
							isProfile
						/>
					</Block>
					{!userMain?.status?.blockMe &&
						!userMain?.status?.blockByMe &&
						!userMain?.frozen && (
							<>
								<Block zIndex={3}>
									<Text color="whiteAlways" size="1rem" bold>
										{secondLine}
									</Text>
								</Block>
								{(userMain.statistics.likes > 0 ||
									userMain.statistics.userFollowers > 0) && (
									<Block flex>
										{userMain.statistics.likes > 0 && (
											<StyledIconContainer
												style={{ marginRight: "1rem" }}
											>
												<Text mr={1}>
													<Icon
														svgIconComponent={
															RegularHeart
														}
													/>
												</Text>
												{pseudoFormat(
													userMain.statistics.likes ||
														null,
												)}
											</StyledIconContainer>
										)}

										{userMain.statistics.userFollowers >
											0 && (
											<StyledIconContainer>
												<Text mr={1}>
													<Icon
														svgIconComponent={
															SolidEye
														}
													/>
												</Text>
												{pseudoFormat(
													userMain.statistics
														.userFollowers || null,
												)}
											</StyledIconContainer>
										)}
									</Block>
								)}

								<Block
									mr={1}
									style={{
										fontSize: "0.8rem",
										color: "var(--dz-sys-icon-background-default)",
									}}
								>
									{(correctOnline === 0 ||
										correctStatus === 4) && (
										<Text
											mr={1}
											style={{
												display: "inline-block",
											}}
										>
											{t(`common:lastActivity`)}
											{": "}
											<DatePresenter
												date={userMain?.active}
												dateOnly
												comma
											/>
										</Text>
									)}
									<Text
										mr={1}
										style={{
											display: "inline-block",
										}}
									>
										{t(`common:accountSince`)}:
									</Text>
									<Text
										style={{
											display: "inline-block",
										}}
									>
										<DatePresenter
											date={userMain?.create}
											dateOnly
										/>
									</Text>
								</Block>
							</>
						)}
				</StyledInfoContainer>
			</Block>
		</HeroSectionDesktopNew>
	);
};

// #####################################################

export default UserHeroSection;

// #####################################################
